import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { useDropzone } from 'react-dropzone';
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { assetsApi } from '../../lib/api';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const expectedColumns = [
  { key: 'name', label: 'Nome', required: true },
  { key: 'asset_type', label: 'Tipo', required: true },
  { key: 'description', label: 'Descrição', required: false },
  { key: 'confidentiality', label: 'Confidencialidade', required: false },
  { key: 'integrity', label: 'Integridade', required: false },
  { key: 'availability', label: 'Disponibilidade', required: false },
  { key: 'department', label: 'Departamento', required: false },
  { key: 'physical_location', label: 'Localização Física', required: false },
  { key: 'ip_address', label: 'IP', required: false },
  { key: 'hostname', label: 'Hostname', required: false },
  { key: 'vendor', label: 'Fabricante', required: false },
  { key: 'model', label: 'Modelo', required: false },
  { key: 'operational_status', label: 'Estado', required: false },
];

const assetTypeMap = {
  'hardware': 'hardware',
  'hw': 'hardware',
  'software': 'software',
  'sw': 'software',
  'dados': 'dados',
  'data': 'dados',
  'servico': 'servico',
  'serviço': 'servico',
  'service': 'servico',
  'pessoas': 'pessoas',
  'people': 'pessoas',
  'instalacao': 'instalacao',
  'instalação': 'instalacao',
  'facility': 'instalacao',
};

export default function AssetImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const downloadTemplate = () => {
    const templateData = [
      {
        'Nome': 'Servidor Exemplo',
        'Tipo': 'hardware',
        'Descrição': 'Descrição do servidor',
        'Confidencialidade': 'confidencial',
        'Integridade': 'alta',
        'Disponibilidade': 'alta',
        'Departamento': 'TI',
        'Localização Física': 'Datacenter - Rack A1',
        'IP': '10.0.1.10',
        'Hostname': 'srv-01.empresa.local',
        'Fabricante': 'Dell',
        'Modelo': 'PowerEdge R750',
        'Estado': 'ativo',
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'template_ativos_nis2.xlsx');
    toast.success('Template descarregado');
  };

  const parseFile = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setErrors([{ row: 0, message: 'O ficheiro não contém dados' }]);
          return;
        }

        const validationErrors = [];
        const validationWarnings = [];
        const processedAssets = [];

        jsonData.forEach((row, index) => {
          const rowNum = index + 2; // +2 because of header and 0-index
          
          // Map columns (case-insensitive)
          const normalizedRow = {};
          Object.keys(row).forEach(key => {
            const normalizedKey = key.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            normalizedRow[normalizedKey] = row[key];
          });

          // Get name
          const name = normalizedRow['nome'] || normalizedRow['name'] || normalizedRow['nome_ativo'];
          if (!name || !name.toString().trim()) {
            validationErrors.push({ row: rowNum, message: 'Nome do ativo é obrigatório' });
            return;
          }

          // Get and validate type
          let assetType = normalizedRow['tipo'] || normalizedRow['type'] || normalizedRow['asset_type'] || 'hardware';
          assetType = assetType.toString().toLowerCase().trim();
          assetType = assetTypeMap[assetType] || 'hardware';

          // Get other fields with defaults
          const confidentiality = normalizedRow['confidencialidade'] || normalizedRow['confidentiality'] || 'interna';
          const integrity = normalizedRow['integridade'] || normalizedRow['integrity'] || 'media';
          const availability = normalizedRow['disponibilidade'] || normalizedRow['availability'] || 'media';
          const status = normalizedRow['estado'] || normalizedRow['status'] || normalizedRow['operational_status'] || 'ativo';

          // Create asset object
          const asset = {
            name: name.toString().trim(),
            asset_type: assetType,
            description: (normalizedRow['descricao'] || normalizedRow['description'] || '').toString().trim(),
            confidentiality: confidentiality.toString().toLowerCase().trim(),
            integrity: integrity.toString().toLowerCase().trim(),
            availability: availability.toString().toLowerCase().trim(),
            department: (normalizedRow['departamento'] || normalizedRow['department'] || '').toString().trim(),
            physical_location: (normalizedRow['localizacao fisica'] || normalizedRow['localizacao_fisica'] || normalizedRow['physical_location'] || '').toString().trim(),
            ip_address: (normalizedRow['ip'] || normalizedRow['ip_address'] || normalizedRow['endereco ip'] || '').toString().trim(),
            hostname: (normalizedRow['hostname'] || '').toString().trim(),
            vendor: (normalizedRow['fabricante'] || normalizedRow['vendor'] || normalizedRow['fornecedor'] || '').toString().trim(),
            model: (normalizedRow['modelo'] || normalizedRow['model'] || '').toString().trim(),
            operational_status: status.toString().toLowerCase().trim(),
          };

          // Warnings for missing recommended fields
          if (!asset.department) {
            validationWarnings.push({ row: rowNum, message: `Departamento não especificado para "${asset.name}"` });
          }

          processedAssets.push(asset);
        });

        setErrors(validationErrors);
        setWarnings(validationWarnings);
        setParsedData(processedAssets);

      } catch (error) {
        console.error('Parse error:', error);
        setErrors([{ row: 0, message: 'Erro ao processar ficheiro: ' + error.message }]);
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setFile(file);
      setImportResult(null);
      setErrors([]);
      setWarnings([]);
      parseFile(file);
    }
  }, [parseFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleImport = async () => {
    if (parsedData.length === 0 || errors.length > 0) return;

    setIsImporting(true);
    try {
      const response = await assetsApi.import(parsedData);
      setImportResult(response.data);
      const created = response.data.imported ?? 0;
      const errs = response.data.errors?.length ?? 0;
      toast.success(`Importação concluída: ${created} ativos criados${errs ? `, ${errs} erros` : ''}`);
    } catch (error) {
      toast.error('Erro ao importar ativos');
      setImportResult({ error: error.message });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Layout
      title={
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/assets')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span>Importar Ativos</span>
        </div>
      }
    >
      <div className="max-w-4xl mx-auto space-y-6" data-testid="asset-import-container">
        {/* Instructions */}
        <Card className="card-flat">
          <CardHeader>
            <CardTitle className="text-lg">Importação de Ativos via Excel</CardTitle>
            <CardDescription>
              Importe ativos em massa utilizando o template Excel compatível com o formato CNCS
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                <Download className="h-4 w-4 mr-2" />
                Descarregar Template
              </Button>
              <span className="text-sm text-slate-500">
                Use o template para garantir a formatação correta
              </span>
            </div>
            <div className="text-sm text-slate-600">
              <p className="font-medium mb-2">Colunas obrigatórias:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Nome</strong> - Nome único do ativo</li>
                <li><strong>Tipo</strong> - hardware, software, dados, servico, pessoas, instalacao</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card className="card-flat">
          <CardContent className="p-6">
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400'}
              `}
              data-testid="file-dropzone"
            >
              <input {...getInputProps()} />
              <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              {isDragActive ? (
                <p className="text-blue-600">Largue o ficheiro aqui...</p>
              ) : (
                <>
                  <p className="text-slate-600 mb-2">
                    Arraste e largue um ficheiro Excel aqui, ou clique para selecionar
                  </p>
                  <p className="text-sm text-slate-400">
                    Formatos suportados: .xlsx, .xls, .csv
                  </p>
                </>
              )}
            </div>

            {file && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg flex items-center gap-3">
                <FileSpreadsheet className="h-6 w-6 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  {parsedData.length} ativos encontrados
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Validation Results */}
        {(errors.length > 0 || warnings.length > 0) && (
          <Card className="card-flat">
            <CardHeader>
              <CardTitle className="text-lg">Validação</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {errors.length > 0 && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertTitle>Erros ({errors.length})</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside mt-2">
                      {errors.slice(0, 5).map((err, i) => (
                        <li key={i}>Linha {err.row}: {err.message}</li>
                      ))}
                      {errors.length > 5 && <li>...e mais {errors.length - 5} erros</li>}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {warnings.length > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Avisos ({warnings.length})</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside mt-2">
                      {warnings.slice(0, 5).map((warn, i) => (
                        <li key={i}>Linha {warn.row}: {warn.message}</li>
                      ))}
                      {warnings.length > 5 && <li>...e mais {warnings.length - 5} avisos</li>}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Preview */}
        {parsedData.length > 0 && errors.length === 0 && (
          <Card className="card-flat">
            <CardHeader>
              <CardTitle className="text-lg">Pré-visualização ({parsedData.length} ativos)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-80">
                <table className="w-full table-dense text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="px-3 py-2 text-left">Nome</th>
                      <th className="px-3 py-2 text-left">Tipo</th>
                      <th className="px-3 py-2 text-left">Departamento</th>
                      <th className="px-3 py-2 text-left">Localização</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 10).map((asset, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="px-3 py-2 font-medium">{asset.name}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline">{asset.asset_type}</Badge>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{asset.department || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{asset.physical_location || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedData.length > 10 && (
                  <p className="text-sm text-slate-500 text-center py-2">
                    ...e mais {parsedData.length - 10} ativos
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Import Result */}
        {importResult && !importResult.error && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800">Importação Concluída</AlertTitle>
            <AlertDescription className="text-green-700">
              <ul className="list-disc list-inside mt-2">
                <li>{importResult.imported ?? 0} ativos criados</li>
                {importResult.errors?.length > 0 && (
                  <li>{importResult.errors.length} erros durante a importação</li>
                )}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate('/assets')}>
            Cancelar
          </Button>
          <Button
            onClick={handleImport}
            disabled={parsedData.length === 0 || errors.length > 0 || isImporting}
            data-testid="import-assets-btn"
          >
            {isImporting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Upload className="mr-2 h-4 w-4" />
            Importar {parsedData.length} Ativos
          </Button>
        </div>
      </div>
    </Layout>
  );
}
